import React, { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '../config';
import { useToast } from '../hooks/use-toast';
import {
  CheckCircle, XCircle, Eye, Clock, CircleNotch, CaretDown,
  PaperPlaneTilt, ArrowClockwise, FileText, Funnel, MagnifyingGlass,
  ArrowUp, Prohibit, Tag, User, Buildings, Lightning
} from '@phosphor-icons/react';

const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'in_review', label: 'In Review' },
  { key: 'approved', label: 'Approved' },
  { key: 'published', label: 'Published' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'unpublished', label: 'Unpublished' },
];

const STATUS_COLOR = {
  draft: 'text-gray-400 bg-gray-500/10',
  submitted: 'text-blue-400 bg-blue-500/10',
  in_review: 'text-amber-400 bg-amber-500/10',
  approved: 'text-emerald-400 bg-emerald-500/10',
  published: 'text-green-400 bg-green-500/10',
  rejected: 'text-red-400 bg-red-500/10',
  unpublished: 'text-gray-400 bg-gray-500/10',
};

const AdminReviewQueue = () => {
  const { toast } = useToast();
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (activeTab) params.append('status', activeTab);
      const res = await fetch(`${BACKEND_URL}/api/marketplace/admin/queue?${params}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
        setTotal(data.total || 0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [activeTab]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/marketplace/admin/queue/stats`, { credentials: 'include' });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchQueue(); fetchStats(); }, [fetchQueue, fetchStats]);

  const doAction = async (id, action, notes = '') => {
    setActionLoading(`${id}-${action}`);
    try {
      const res = await fetch(`${BACKEND_URL}/api/marketplace/admin/review/${id}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });
      if (res.ok) {
        toast({ title: `${action.charAt(0).toUpperCase() + action.slice(1)}d`, description: `Submission ${action}d successfully.` });
        fetchQueue();
        fetchStats();
        setReviewNotes('');
      } else {
        const err = await res.json();
        toast({ title: 'Error', description: err.detail || `Failed to ${action}`, variant: 'destructive' });
      }
    } catch { toast({ title: 'Error', description: 'Network error', variant: 'destructive' }); }
    finally { setActionLoading(null); }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-0)] p-6" data-testid="admin-review-queue">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--text-primary)]">Marketplace Review Queue</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">Review and manage automation submissions for the marketplace.</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-6 gap-3 mb-6" data-testid="queue-stats">
            {STATUS_TABS.filter(t => t.key).map(tab => (
              <div key={tab.key} className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-[var(--text-primary)]">{stats.by_status?.[tab.key] || 0}</p>
                <p className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">{tab.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 p-1 bg-[var(--surface-1)] rounded-xl border border-[var(--border-default)] overflow-x-auto" data-testid="status-tabs">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key || 'all'}`}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-[var(--brand-primary)] text-white'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-2)]'
              }`}
            >
              {tab.label}
              {tab.key && stats?.by_status?.[tab.key] ? ` (${stats.by_status[tab.key]})` : ''}
            </button>
          ))}
        </div>

        {/* Queue List */}
        {loading ? (
          <div className="flex justify-center py-16"><CircleNotch size={28} className="animate-spin text-[var(--brand-primary)]" /></div>
        ) : submissions.length === 0 ? (
          <div className="text-center py-16 bg-[var(--surface-1)] rounded-xl border border-[var(--border-default)]">
            <CheckCircle size={40} className="mx-auto text-[var(--text-muted)] mb-3" />
            <p className="text-[var(--text-secondary)] text-sm">{activeTab ? `No ${activeTab} submissions` : 'No submissions in queue'}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="queue-list">
            {submissions.map(sub => {
              const isExpanded = expanded === sub.submission_id;
              return (
                <div key={sub.submission_id} className="bg-[var(--surface-1)] border border-[var(--border-default)] rounded-xl overflow-hidden hover:border-[var(--border-strong)] transition-colors" data-testid={`queue-item-${sub.submission_id}`}>
                  {/* Row */}
                  <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => { setExpanded(isExpanded ? null : sub.submission_id); setReviewNotes(''); }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-semibold text-[var(--text-primary)] truncate">{sub.name}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLOR[sub.status]}`}>{sub.status}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs text-[var(--text-muted)] flex items-center gap-1"><User size={10} />{sub.creator_name || 'Unknown'}</span>
                        {sub.category && <span className="text-xs text-[var(--text-muted)]">{sub.category}</span>}
                        {sub.submitted_at && <span className="text-xs text-[var(--text-muted)]">Submitted {new Date(sub.submitted_at).toLocaleDateString()}</span>}
                      </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                      {(sub.status === 'submitted' || sub.status === 'in_review') && (
                        <>
                          <button data-testid={`approve-btn-${sub.submission_id}`} onClick={() => doAction(sub.submission_id, 'approve')} disabled={actionLoading} className="px-3 py-1.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-xs font-medium hover:bg-emerald-500/20 transition-colors disabled:opacity-50">
                            {actionLoading === `${sub.submission_id}-approve` ? <CircleNotch size={12} className="animate-spin" /> : <><CheckCircle size={12} className="inline mr-1" />Approve</>}
                          </button>
                          <button data-testid={`reject-btn-${sub.submission_id}`} onClick={() => setExpanded(sub.submission_id)} className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-colors">
                            <XCircle size={12} className="inline mr-1" />Reject
                          </button>
                        </>
                      )}
                      {sub.status === 'approved' && (
                        <button data-testid={`publish-btn-${sub.submission_id}`} onClick={() => doAction(sub.submission_id, 'publish')} disabled={actionLoading} className="px-3 py-1.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg text-xs font-medium hover:bg-green-500/20 transition-colors disabled:opacity-50">
                          {actionLoading === `${sub.submission_id}-publish` ? <CircleNotch size={12} className="animate-spin" /> : <><ArrowUp size={12} className="inline mr-1" />Publish</>}
                        </button>
                      )}
                      {sub.status === 'published' && (
                        <button data-testid={`unpublish-btn-${sub.submission_id}`} onClick={() => doAction(sub.submission_id, 'unpublish')} disabled={actionLoading} className="px-3 py-1.5 bg-gray-500/10 text-gray-400 border border-gray-500/20 rounded-lg text-xs font-medium hover:bg-gray-500/20 transition-colors disabled:opacity-50">
                          <Prohibit size={12} className="inline mr-1" />Unpublish
                        </button>
                      )}
                      {sub.status === 'unpublished' && (
                        <button data-testid={`republish-btn-${sub.submission_id}`} onClick={() => doAction(sub.submission_id, 'republish')} disabled={actionLoading} className="px-3 py-1.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg text-xs font-medium hover:bg-green-500/20 transition-colors disabled:opacity-50">
                          <ArrowClockwise size={12} className="inline mr-1" />Republish
                        </button>
                      )}
                      <CaretDown size={14} className={`text-[var(--text-muted)] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>

                  {/* Detail Panel */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-[var(--border-default)] pt-4 space-y-3" data-testid={`detail-panel-${sub.submission_id}`}>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Description</p>
                          <p className="text-xs text-[var(--text-secondary)]">{sub.description || 'No description'}</p>
                        </div>
                        <div className="space-y-2">
                          <div><p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Creator</p><p className="text-xs text-[var(--text-primary)]">{sub.creator_name}</p></div>
                          <div><p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Difficulty</p><p className="text-xs text-[var(--text-primary)] capitalize">{sub.difficulty_level}</p></div>
                          <div><p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Version</p><p className="text-xs text-[var(--text-primary)]">v{sub.version || 1}</p></div>
                        </div>
                      </div>
                      {sub.capabilities?.length > 0 && (
                        <div><p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Capabilities</p><div className="flex flex-wrap gap-1">{sub.capabilities.map(c => <span key={c} className="px-2 py-0.5 bg-[var(--surface-3)] rounded text-[10px] text-[var(--text-muted)]">{c}</span>)}</div></div>
                      )}
                      {sub.integrations?.length > 0 && (
                        <div><p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Integrations</p><div className="flex flex-wrap gap-1">{sub.integrations.map(i => <span key={i} className="px-2 py-0.5 bg-amber-500/10 text-amber-400 rounded text-[10px]">{i}</span>)}</div></div>
                      )}
                      {sub.tags?.length > 0 && (
                        <div><p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Tags</p><div className="flex flex-wrap gap-1">{sub.tags.map(t => <span key={t} className="px-2 py-0.5 bg-[var(--surface-3)] rounded text-[10px] text-[var(--text-muted)]">{t}</span>)}</div></div>
                      )}
                      {sub.review_notes && (
                        <div className="p-3 bg-[var(--surface-2)] rounded-lg border border-[var(--border-default)]">
                          <p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Review Notes</p>
                          <p className="text-xs text-[var(--text-secondary)]">{sub.review_notes}</p>
                        </div>
                      )}

                      {/* Reject with Notes */}
                      {(sub.status === 'submitted' || sub.status === 'in_review') && (
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Rejection Notes</p>
                            <textarea
                              data-testid={`reject-notes-${sub.submission_id}`}
                              placeholder="Provide feedback for the creator..."
                              value={reviewNotes}
                              onChange={e => setReviewNotes(e.target.value)}
                              rows={2}
                              className="w-full px-3 py-2 bg-[var(--surface-3)] border border-[var(--border-default)] rounded-lg text-xs text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:outline-none focus:border-red-500/50 resize-none"
                            />
                          </div>
                          <button
                            data-testid={`reject-confirm-${sub.submission_id}`}
                            onClick={() => doAction(sub.submission_id, 'reject', reviewNotes)}
                            disabled={actionLoading}
                            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
                          >
                            {actionLoading === `${sub.submission_id}-reject` ? <CircleNotch size={12} className="animate-spin" /> : 'Reject'}
                          </button>
                        </div>
                      )}

                      {/* Audit Trail */}
                      {sub.audit_trail?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-[var(--text-muted)] uppercase mb-1">Audit Trail</p>
                          <div className="space-y-1">
                            {sub.audit_trail.slice(-5).reverse().map((entry, i) => (
                              <div key={i} className="flex items-center gap-2 text-[10px] text-[var(--text-muted)]">
                                <span className="text-[var(--text-secondary)] capitalize">{entry.action}</span>
                                <span>by {entry.user_name}</span>
                                <span>{new Date(entry.timestamp).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviewQueue;
